import * as React from 'react';
import { compose, setDisplayName, withState, withHandlers } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import * as moment from 'moment-timezone';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { State } from 'reducers';
import { connect } from 'react-redux';
import { UtcTime } from 'types';
import { Input } from 'ui/shared/Input';
import { Required } from 'ui/pages/designer/Required';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const marginBottom = {
    marginBottom: '1em',
};

const RawScheduleRangeStart = ({
    date,
    time,
    handleChangeDate,
    handleChangeTime,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <div style={ marginBottom }>
        <label htmlFor="edit-report-schedule-range-start">
            { t('WC_REPORT_SCHEDULE_RANGE_START') }
        </label>
        <Input
            id="edit-report-schedule-range-start-date"
            value={ date }
            onChange={ handleChangeDate }
            maxLength={ 10 }
            placeholder={ t('WC_REPORT_SCHEDULE_DATE_PLACEHOLDER') }
        />
        <Input
            id="edit-report-schedule-range-start-time"
            value={ time }
            onChange={ handleChangeTime }
            maxLength={ 5 }
            placeholder={ t('WC_REPORT_SCHEDULE_TIME_PLACEHOLDER') }
        />
        <Required visible={ !date || !time } />
    </div>
;

type ChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => void;

type RecomposeProps = {
    date: string;
    time: string;
    handleChangeDate: ChangeHandler;
    handleChangeTime: ChangeHandler;
};
type _RecomposeProps = RecomposeProps & {
    _setDate: (s: string) => void;
    _setTime: (s: string) => void;
};
type StateProps = {
    tzOffsetCalculator: (t: UtcTime) => number;
};

const dateNumberToString = (utcTime: UtcTime, tzOffsetCalculator: (t: UtcTime) => number): string => {
    const m =
        utcTime
            ? moment.utc(utcTime).utcOffset(tzOffsetCalculator(utcTime))
            : moment.utc().utcOffset(tzOffsetCalculator(Date.now()));
    
    return m.format('YYYY-MM-DD');
};

const timeNumberToString = (serverTime: UtcTime, tzOffsetCalculator: (t: UtcTime) => number): string => {
    if (!serverTime) {
        return '00:00';
    }
    return moment.utc(serverTime).utcOffset(tzOffsetCalculator(serverTime)).format('HH:mm');
};

const update = ({ onChange, date, time, tzOffsetCalculator }: OwnProps & StateProps & _RecomposeProps) => {
    const utcStart = moment.utc(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
    if (utcStart.isValid()) {
        const serverStart = utcStart.clone().utcOffset(tzOffsetCalculator(utcStart.valueOf()), true);
        onChange(reportSettings => reportSettings.setScheduleRangeStart(serverStart.valueOf()));
    }
    else {
        onChange(reportSettings => reportSettings.setScheduleRangeStart(0));
    }
};

const mapStateToProps = (state: State) => ({
    tzOffsetCalculator: getTimezoneOffsetCalculator(state),
});

export const ScheduleRangeStart = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('ScheduleRangeStart'),
    translate(),
    connect<StateProps, {}, {}>(mapStateToProps),
    withState('date', '_setDate', ({ reportSettings, tzOffsetCalculator }: OwnProps & StateProps) =>
        dateNumberToString(reportSettings.scheduleRangeStart, tzOffsetCalculator)
    ),
    withState('time', '_setTime', ({ reportSettings, tzOffsetCalculator }: OwnProps & StateProps) =>
        timeNumberToString(reportSettings.scheduleRangeStart, tzOffsetCalculator)
    ),
    withHandlers<OwnProps & StateProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeDate: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const date = e.target.value;
            props._setDate(date);
            update({ ...props, date });
        },
        handleChangeTime: props => (e: React.ChangeEvent<HTMLInputElement>) => {
            const time = e.target.value;
            props._setTime(time);
            update({ ...props, time });
        },
    }),
)(RawScheduleRangeStart);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/ScheduleRangeStart.tsx